import React, { useState } from 'react';
import {
    FileText,
    Download,
    Loader2,
    X
} from 'lucide-react';

import { useToast } from './toastContext';
import { DEVICE_TYPE_MAP } from './houseHelpers';

const ProgrammingCardModal = ({
    nodes,
    selectedNode,
    onClose,
    onGenerate,
    onGenerateTypeB
}) => {
    const toast = useToast();

    const [currentNode, setCurrentNode] = useState(
        selectedNode || (nodes.length === 1 ? nodes[0] : null)
    );

    const [isGenerating, setIsGenerating] = useState(false);
    const [cardType, setCardType] = useState('A');

    const handleGenerate = async () => {
        if (!currentNode) {
            toast.warning('Выберите узел учёта');
            return;
        }

        setIsGenerating(true);

        try {
            if (cardType === 'B') {
                await onGenerateTypeB(currentNode);
            } else {
                await onGenerate(currentNode);
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const hc = currentNode?.heatComputer;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <FileText className="w-5 h-5 text-purple-400" />
                        Карта программирования
                    </h3>

                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="flex gap-2 mb-2">
                        <button
                            onClick={() => setCardType('A')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition border ${
                                cardType === 'A'
                                    ? 'bg-purple-600 border-purple-500 text-white'
                                    : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                            }`}
                        >
                            Тип A (Стандартная)
                        </button>

                        <button
                            onClick={() => setCardType('B')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition border ${
                                cardType === 'B'
                                    ? 'bg-purple-600 border-purple-500 text-white'
                                    : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                            }`}
                        >
                            Тип B (Приложение А)
                        </button>
                    </div>

                    {nodes.length > 1 && (
                        <div>
                            <label className="block text-slate-400 text-xs mb-1">
                                Узел учёта *
                            </label>

                            <select
                                value={currentNode?.id || ''}
                                onChange={(e) => {
                                    const node = nodes.find((n) => n.id === parseInt(e.target.value, 10));

                                    setCurrentNode(node || null);
                                }}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                            >
                                <option value="">— Выберите узел —</option>

                                {nodes.map((n) => (
                                    <option key={n.id} value={n.id}>
                                        {n.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {currentNode && (
                        <>
                            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <h4 className="text-white font-medium text-sm mb-3">
                                    Тепловычислитель
                                </h4>

                                {hc ? (
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <span className="text-slate-400">Исполнение (Модель):</span>{' '}
                                            <span className="text-white">
                                                {hc.deviceModel ||
                                                    DEVICE_TYPE_MAP[hc.deviceType] ||
                                                    hc.deviceType ||
                                                    '—'}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-slate-400">Описание:</span>{' '}
                                            <span className="text-white">
                                                {hc.displayName || hc.deviceName || '—'}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-slate-400">Зав. номер:</span>{' '}
                                            <span className="text-white">
                                                {hc.factoryNumber || '—'}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-slate-400">Дата выпуска:</span>{' '}
                                            <span className="text-white">
                                                {hc.releaseDate || '—'}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-slate-400">CRC16:</span>{' '}
                                            <span className="text-white">
                                                {hc.crc16 || '—'}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-slate-400">Перемычка:</span>{' '}
                                            <span className="text-white">
                                                {hc.jumperPosition || '—'}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-slate-400">Архивы:</span>{' '}
                                            <span className="text-white">
                                                {hc.archiveDepth || '—'}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-slate-400">Алгоритм:</span>{' '}
                                            <span className="text-white">
                                                {hc.algorithmFormula || '—'}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-slate-500 text-xs">
                                        Тепловычислитель не добавлен
                                    </div>
                                )}
                            </div>

                            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <h4 className="text-white font-medium text-sm mb-3">
                                    Датчики
                                </h4>

                                <div className="text-xs text-slate-400">
                                    • Температуры: {currentNode.temperatureSensors.length}<br />
                                    • Давления: {currentNode.pressureSensors.length}<br />
                                    • Расходомеры: {currentNode.flowMeters.length}
                                </div>
                            </div>

                            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <h4 className="text-white font-medium text-sm mb-3">
                                    Ответственные
                                </h4>

                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                        <span className="text-slate-400">Организация:</span>{' '}
                                        <span className="text-white">
                                            {currentNode.responsibleOrg || '—'}
                                        </span>
                                    </div>

                                    <div>
                                        <span className="text-slate-400">ФИО:</span>{' '}
                                        <span className="text-white">
                                            {currentNode.responsibleName || '—'}
                                        </span>
                                    </div>

                                    <div>
                                        <span className="text-slate-400">УК:</span>{' '}
                                        <span className="text-white">
                                            {currentNode.managementOrg || '—'}
                                        </span>
                                    </div>

                                    <div>
                                        <span className="text-slate-400">Директор:</span>{' '}
                                        <span className="text-white">
                                            {currentNode.managementName || '—'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-xs text-yellow-200">
                                Будет сформирован документ Word (.docx) с картой программирования
                                на основе введённых данных.
                            </div>
                        </>
                    )}
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-slate-800">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                    >
                        Отмена
                    </button>

                    <button
                        onClick={handleGenerate}
                        disabled={isGenerating || !currentNode}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Формирование...
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                Сформировать карту
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProgrammingCardModal;